import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import api from '../api/axios';

interface TenantBonusConfig {
  id: number;
  tenant_id: number;
  credit_rounding_method: string;
  bonus_percentage: number;
  bonus_rounding: string;
  bonus_to_credit: boolean;
  loyalty_percentage: number;
  vip_percentage: number;
  rakeback_enabled: boolean;
  rakeback_percentage: number;
  happy_hour_enabled: boolean;
  happy_hour_percentage: number;
  happy_hour_start_time: string | null;
  happy_hour_end_time: string | null;
  happy_hour_days: number[];
  happy_hour_manual_active: boolean;
  happy_hour_manual_started_at: string | null;
  created_at: string;
  updated_at: string;
}

// Backend stores happy_hour_days as 1 = Monday .. 7 = Sunday.
const DAY_OPTIONS = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 7, label: 'Sun' },
];

interface HappyHourStatus {
  tenant_id: number;
  happy_hour_active: boolean;
  manual_active: boolean;
  manual_started_at: string | null;
  scheduled_enabled: boolean;
  scheduled_active: boolean;
  scheduled_times: {
    start: string | null;
    end: string | null;
    days: number[];
  };
}

const TenantBonusConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<TenantBonusConfig[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<TenantBonusConfig | null>(null);
  const [happyHourStatus, setHappyHourStatus] = useState<HappyHourStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [pendingChanges, setPendingChanges] = useState<Partial<TenantBonusConfig>>({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Check if user is a super admin
  const userRole = localStorage.getItem('userRole');
  const isSuperAdmin = userRole === 'super_admin';


  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      console.log('TenantBonusConfigPage - fetchConfigs - API base URL:', api.defaults.baseURL);
      console.log('TenantBonusConfigPage - fetchConfigs - Full URL:', api.defaults.baseURL + '/api/v1/admin/tenant-configs/');
      
      const response = await api.get('/api/v1/admin/tenant-configs/');
      console.log('TenantBonusConfigPage - fetchConfigs - Response:', response.data);
      
      const data = response.data as TenantBonusConfig[];
      console.log('TenantBonusConfigPage - fetchConfigs - Data received:', data);
      console.log('TenantBonusConfigPage - fetchConfigs - Data length:', data.length);
      console.log('TenantBonusConfigPage - fetchConfigs - User role:', userRole);
      console.log('TenantBonusConfigPage - fetchConfigs - Is super admin:', isSuperAdmin);
      
      setConfigs(data);
      if (data.length > 0) {
        setSelectedConfig(data[0]);
        fetchHappyHourStatus(data[0].tenant_id);
      } else {
        console.log('TenantBonusConfigPage - fetchConfigs - No data received, showing empty state');
      }
    } catch (err) {
      console.error('TenantBonusConfigPage - fetchConfigs - Error:', err);
      setError('Failed to load tenant configurations');
    } finally {
      setLoading(false);
    }
  };

  const fetchHappyHourStatus = async (tenantId: number) => {
    try {
      const response = await api.get(`/api/v1/admin/tenant-configs/${tenantId}/happy-hour/status/`);
      setHappyHourStatus(response.data as HappyHourStatus);
    } catch (err) {
      console.error('Failed to fetch Happy Hour status:', err);
      setHappyHourStatus(null);
      setError('Failed to load Happy Hour status. Start/Stop are unavailable until it loads.');
    }
  };

  const createDefaultConfig = async () => {
    setSaving(true);
    setError(null);
    
    try {
      console.log('TenantBonusConfigPage - createDefaultConfig - Creating default config for tenant admin');
      
      const defaultConfig = {
        credit_rounding_method: "standard",
        bonus_percentage: 0.0,
        bonus_rounding: "nearest",
        bonus_to_credit: false,
        loyalty_percentage: 0.0,
        vip_percentage: 0.0,
        rakeback_enabled: false,
        rakeback_percentage: 5.0,
        happy_hour_enabled: false,
        happy_hour_percentage: 0.0,
        happy_hour_start_time: null,
        happy_hour_end_time: null,
        happy_hour_days: []
      };
      
      // Get the user's tenant ID from the API response
      // First, get the current user info to get their tenant_id
      const userResponse = await api.get('/api/v1/users/me');
      const user = userResponse.data as any;
      const tenantId = user.tenant_id;
      
      console.log('TenantBonusConfigPage - createDefaultConfig - User tenant ID:', tenantId);
      
      const response = await api.post('/api/v1/admin/tenant-configs/', {
        ...defaultConfig,
        tenant_id: tenantId
      });
      console.log('TenantBonusConfigPage - createDefaultConfig - Response:', response.data);
      
      const newConfig = response.data as TenantBonusConfig;
      setConfigs([newConfig]);
      setSelectedConfig(newConfig);
      setSuccess('Default configuration created successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('TenantBonusConfigPage - createDefaultConfig - Error:', err);
      setError('Failed to create default configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field: keyof TenantBonusConfig, value: any) => {
    console.log(`🔄 handleFieldChange called: ${field} = ${value}`);
    setPendingChanges(prev => {
      const updated = { ...prev, [field]: value };
      console.log(`📝 pendingChanges updated:`, updated);
      return updated;
    });
    setHasChanges(true);
    console.log(`✅ hasChanges set to true`);
  };

  const submitChanges = async () => {
    console.log(`🚀 submitChanges called. selectedConfig:`, selectedConfig, `hasChanges:`, hasChanges, `pendingChanges:`, pendingChanges);
    if (!selectedConfig || !hasChanges) {
      console.log(`❌ submitChanges aborted: selectedConfig=${!!selectedConfig}, hasChanges=${hasChanges}`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      console.log(`📤 Sending PUT request with pendingChanges:`, pendingChanges);
      const response = await api.put(`/api/v1/admin/tenant-configs/${selectedConfig.tenant_id}/`, pendingChanges);
      console.log(`✅ Response received:`, response.data);
      const updatedConfig = response.data as TenantBonusConfig;
      setSelectedConfig(updatedConfig);
      setConfigs(configs.map(c => c.tenant_id === updatedConfig.tenant_id ? updatedConfig : c));
      setPendingChanges({});
      setHasChanges(false);
      setSuccess('Configuration updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error(`❌ Error updating config:`, err);
      setError('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = async (config: Partial<TenantBonusConfig>) => {
    if (!selectedConfig) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const response = await api.put(`/api/v1/admin/tenant-configs/${selectedConfig.tenant_id}/`, config);
      const updatedConfig = response.data as TenantBonusConfig;
      setSelectedConfig(updatedConfig);
      setConfigs(configs.map(c => c.tenant_id === updatedConfig.tenant_id ? updatedConfig : c));
      setSuccess('Configuration updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const startManualHappyHour = async () => {
    if (!selectedConfig) return;
    
    try {
      await api.post(`/api/v1/admin/tenant-configs/${selectedConfig.tenant_id}/happy-hour/start/`);
      setSuccess('Happy Hour started!');
      fetchHappyHourStatus(selectedConfig.tenant_id);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to start Happy Hour');
    }
  };

  const stopManualHappyHour = async (disableSchedule = false) => {
    if (!selectedConfig) return;
    
    try {
      const response = await api.post(
        `/api/v1/admin/tenant-configs/${selectedConfig.tenant_id}/happy-hour/stop/`,
        { disable_schedule: disableSchedule }
      );
      const result = response.data as { message?: string; happy_hour_active?: boolean };
      // Clearing the manual override does not end a Happy Hour that is inside its
      // scheduled window, so trust the state the API reports back.
      if (result.happy_hour_active) {
        setSuccess(null);
        setError(result.message || 'Happy Hour is still active via the recurring schedule.');
      } else {
        setError(null);
        setSuccess('Happy Hour stopped!');
        setTimeout(() => setSuccess(null), 3000);
      }
      fetchHappyHourStatus(selectedConfig.tenant_id);
    } catch (err) {
      setError('Failed to stop Happy Hour');
    }
  };

  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5); // HH:MM format
  };

  const formatDays = (days: number[]) => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => dayNames[day - 1]).join(', ');
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        times.push({ value: timeString, label: displayTime });
      }
    }
    return times;
  };

  const getCurrentValue = (field: keyof TenantBonusConfig) => {
    return pendingChanges[field] !== undefined ? pendingChanges[field] : selectedConfig?.[field];
  };

  const scheduleEnabled = Boolean(getCurrentValue('happy_hour_enabled'));
  const selectedHappyHourDays = (getCurrentValue('happy_hour_days') as number[]) || [];
  // Without a loaded status we cannot tell whether Happy Hour is running, so neither
  // Start nor Stop is safe to offer.
  const happyHourStatusUnknown = !happyHourStatus;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading tenant configurations...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {isSuperAdmin ? 'Tenant Bonus Configuration' : 'Bonus Configuration'}
        </Typography>
        {isSuperAdmin && (
          <Box display="flex" gap={1}>
            {configs.map((config) => (
              <Button
                key={config.tenant_id}
                variant={selectedConfig?.tenant_id === config.tenant_id ? "contained" : "outlined"}
                onClick={() => {
                  setSelectedConfig(config);
                  fetchHappyHourStatus(config.tenant_id);
                }}
              >
                Tenant {config.tenant_id}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : selectedConfig ? (
        <Paper sx={{ p: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Basic Configuration" icon={<SettingsIcon />} />
            <Tab label="Happy Hour" icon={<ScheduleIcon />} />
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <Card>
                  <CardHeader title="Bonus Settings" />
                  <CardContent>
                    <TextField
                      fullWidth
                      label="Bonus Percentage"
                      type="number"
                      value={getCurrentValue('bonus_percentage') || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                          handleFieldChange('bonus_percentage', value === '' ? 0 : parseFloat(value));
                        }
                      }}
                      disabled={saving}
                      inputProps={{ 
                        min: 0, 
                        max: 100, 
                        step: 0.01,
                        style: { MozAppearance: 'textfield' }
                      }}
                      sx={{ 
                        mb: 2,
                        '& input[type=number]': {
                          MozAppearance: 'textfield'
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        }
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(getCurrentValue('bonus_to_credit'))}
                          onChange={(e) => handleFieldChange('bonus_to_credit', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label="Add Bonus to Credit Balance"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: -1 }}>
                      When enabled, bonus amounts will be added to the credit balance instead of a separate bonus balance
                    </Typography>
                    <TextField
                      fullWidth
                      label="Loyalty Percentage"
                      type="number"
                      value={getCurrentValue('loyalty_percentage') || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                          handleFieldChange('loyalty_percentage', value === '' ? 0 : parseFloat(value));
                        }
                      }}
                      disabled={saving}
                      inputProps={{ 
                        min: 0, 
                        max: 100, 
                        step: 0.01,
                        style: { MozAppearance: 'textfield' }
                      }}
                      sx={{ 
                        mb: 2,
                        '& input[type=number]': {
                          MozAppearance: 'textfield'
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="VIP Percentage"
                      type="number"
                      value={getCurrentValue('vip_percentage') || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                          handleFieldChange('vip_percentage', value === '' ? 0 : parseFloat(value));
                        }
                      }}
                      disabled={saving}
                      inputProps={{ 
                        min: 0, 
                        max: 100, 
                        step: 0.01,
                        style: { MozAppearance: 'textfield' }
                      }}
                      sx={{ 
                        '& input[type=number]': {
                          MozAppearance: 'textfield'
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        }
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(getCurrentValue('rakeback_enabled'))}
                          onChange={(e) => handleFieldChange('rakeback_enabled', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label="Enable RakeBack System"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: -1 }}>
                      When enabled, show RakeBack card instead of Loyalty Points in player portal
                    </Typography>
                    {getCurrentValue('rakeback_enabled') && (
                      <TextField
                        fullWidth
                        label="RakeBack Percentage"
                        type="number"
                        value={getCurrentValue('rakeback_percentage') || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                            handleFieldChange('rakeback_percentage', value === '' ? 0 : parseFloat(value));
                          }
                        }}
                        disabled={saving}
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 0.01,
                          style: { MozAppearance: 'textfield' }
                        }}
                        sx={{
                          mb: 2,
                          '& input[type=number]': {
                            MozAppearance: 'textfield'
                          },
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0
                          }
                        }}
                      />
                    )}
                    {hasChanges && (
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={submitChanges}
                          disabled={saving}
                        >
                          Save Changes
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setPendingChanges({});
                            setHasChanges(false);
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <Card>
                  <CardHeader title="Rounding Settings" />
                  <CardContent>
                    <TextField
                      fullWidth
                      select
                      label="Bonus Rounding"
                      value={selectedConfig.bonus_rounding}
                      onChange={(e) => updateConfig({ bonus_rounding: e.target.value })}
                      disabled={saving}
                      SelectProps={{ native: true }}
                    >
                      <option value="down">Round Down</option>
                      <option value="up">Round Up</option>
                      <option value="nearest">Round to Nearest</option>
                    </TextField>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <Card>
                  <CardHeader title="Scheduled Happy Hour" />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Times are calculated in EST/EDT (America/New_York)
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={scheduleEnabled}
                          onChange={(e) => handleFieldChange('happy_hour_enabled', e.target.checked)}
                          disabled={saving}
                        />
                      }
                      label="Enable Scheduled Happy Hour"
                      sx={{ mb: 2 }}
                    />

                    <Box
                      component="fieldset"
                      disabled={!scheduleEnabled}
                      sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
                    >
                        <TextField
                          fullWidth
                          label="Happy Hour Bonus %"
                          type="number"
                          value={getCurrentValue('happy_hour_percentage') || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 1000)) {
                              handleFieldChange('happy_hour_percentage', value === '' ? 0 : parseFloat(value));
                            }
                          }}
                          disabled={saving}
                          inputProps={{
                            min: 0,
                            max: 1000,
                            step: 0.01,
                            style: { MozAppearance: 'textfield' }
                          }}
                          sx={{ 
                            mb: 2,
                            '& input[type=number]': {
                              MozAppearance: 'textfield'
                            },
                            '& input[type=number]::-webkit-outer-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            },
                            '& input[type=number]::-webkit-inner-spin-button': {
                              WebkitAppearance: 'none',
                              margin: 0
                            }
                          }}
                        />
                        <Box display="flex" gap={2}>
                          <TextField
                            select
                            label="Start Time"
                            value={getCurrentValue('happy_hour_start_time') || ''}
                            onChange={(e) => handleFieldChange('happy_hour_start_time', e.target.value)}
                            disabled={saving}
                            SelectProps={{ native: true }}
                            sx={{ flex: 1 }}
                            InputLabelProps={{ shrink: true }}
                          >
                            <option value="">Select time</option>
                            {generateTimeOptions().map((time) => (
                              <option key={time.value} value={time.value}>
                                {time.label}
                              </option>
                            ))}
                          </TextField>
                          <TextField
                            select
                            label="End Time"
                            value={getCurrentValue('happy_hour_end_time') || ''}
                            onChange={(e) => handleFieldChange('happy_hour_end_time', e.target.value)}
                            disabled={saving}
                            SelectProps={{ native: true }}
                            sx={{ flex: 1 }}
                            InputLabelProps={{ shrink: true }}
                          >
                            <option value="">Select time</option>
                            {generateTimeOptions().map((time) => (
                              <option key={time.value} value={time.value}>
                                {time.label}
                              </option>
                            ))}
                          </TextField>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Days of week
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1}>
                            {DAY_OPTIONS.map((day) => {
                              const isSelected = selectedHappyHourDays.includes(day.value);
                              return (
                                <Chip
                                  key={day.value}
                                  label={day.label}
                                  color={isSelected ? 'primary' : 'default'}
                                  variant={isSelected ? 'filled' : 'outlined'}
                                  disabled={saving}
                                  onClick={() => {
                                    const next = isSelected
                                      ? selectedHappyHourDays.filter((d) => d !== day.value)
                                      : [...selectedHappyHourDays, day.value].sort((a, b) => a - b);
                                    handleFieldChange('happy_hour_days', next);
                                  }}
                                />
                              );
                            })}
                          </Box>
                          {scheduleEnabled && selectedHappyHourDays.length === 0 && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                              No days selected — the scheduled Happy Hour will never run.
                            </Typography>
                          )}
                        </Box>
                    </Box>
                        {hasChanges && (
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={submitChanges}
                              disabled={saving}
                            >
                              Save Changes
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setPendingChanges({});
                                setHasChanges(false);
                              }}
                              disabled={saving}
                            >
                              Cancel
                            </Button>
                          </Box>
                        )}
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
                <Card>
                  <CardHeader title="Manual Happy Hour" />
                  <CardContent>
                    <TextField
                      fullWidth
                      label="Manual Happy Hour Bonus %"
                      type="number"
                      value={getCurrentValue('happy_hour_percentage') || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0 && parseFloat(value) <= 1000)) {
                          handleFieldChange('happy_hour_percentage', value === '' ? 0 : parseFloat(value));
                        }
                      }}
                      disabled={saving}
                      inputProps={{
                        min: 0,
                        max: 1000,
                        step: 0.01,
                        style: { MozAppearance: 'textfield' }
                      }}
                      sx={{ 
                        mb: 2,
                        '& input[type=number]': {
                          MozAppearance: 'textfield'
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0
                        }
                      }}
                    />
                    
                    {happyHourStatus && (
                      <Box mb={2}>
                        <Chip
                          label={
                            happyHourStatus.happy_hour_active
                              ? happyHourStatus.manual_active
                                ? "Active (manual override)"
                                : "Active (scheduled)"
                              : "Inactive"
                          }
                          color={happyHourStatus.happy_hour_active ? "success" : "default"}
                          icon={happyHourStatus.happy_hour_active ? <CheckCircleIcon /> : <ErrorIcon />}
                        />
                        {happyHourStatus.manual_started_at && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Started: {new Date(happyHourStatus.manual_started_at).toLocaleString()}
                          </Typography>
                        )}
                        {happyHourStatus.scheduled_active && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Running from the recurring schedule
                            {happyHourStatus.scheduled_times.start && happyHourStatus.scheduled_times.end
                              ? ` (${happyHourStatus.scheduled_times.start}–${happyHourStatus.scheduled_times.end} EST/EDT)`
                              : ''}
                            . Use "Stop &amp; Disable Schedule" to end it.
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {hasChanges && (
                      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={submitChanges}
                          disabled={saving}
                        >
                          Save Percentage
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setPendingChanges({});
                            setHasChanges(false);
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}

                    <Box display="flex" gap={1}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PlayIcon />}
                        onClick={startManualHappyHour}
                        disabled={saving || happyHourStatusUnknown || happyHourStatus?.happy_hour_active}
                      >
                        Start Happy Hour
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={() => stopManualHappyHour(false)}
                        disabled={saving || happyHourStatusUnknown || !happyHourStatus?.happy_hour_active}
                      >
                        Stop Happy Hour
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<StopIcon />}
                        onClick={() => stopManualHappyHour(true)}
                        disabled={saving || happyHourStatusUnknown || !happyHourStatus?.happy_hour_active}
                      >
                        Stop &amp; Disable Schedule
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Bonus Configuration Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {isSuperAdmin 
              ? 'No tenant bonus configurations have been created yet.' 
              : 'Your tenant does not have a bonus configuration set up yet.'}
          </Typography>
          {!isSuperAdmin && (
            <Button
              variant="contained"
              color="primary"
              onClick={createDefaultConfig}
              disabled={saving}
              sx={{ mt: 2 }}
            >
              Create Default Configuration
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default TenantBonusConfigPage;
